import { useMutation } from '@src/query';


/**
 * Lead thread mutation hooks (M24, AUTOORBIT-316). Each wraps the shared
 * `useMutation` engine with the injected-`queryClient` `mutateFn({ apiClient,
 * queryClient })` contract — the corelib query singleton is NOT imported here.
 * Invalidation runs inside `mutateFn` (the one place `queryClient` is injected)
 * so the seller's leads cache prefix `['root','','leads',sellerId]` is preserved
 * (list tab counts + rows refresh after every write).
 *
 * Reply / status / convert each return the refreshed thread detail; the caller
 * replaces its selected-thread state from that response (no extra refetch).
 * Mirrors the Customers/Garage `query/<area>/use<Thing>Mutations.js` module shape.
 */

/** Cache key prefix for a seller's leads list, shared across the tab queries. */
function leadsKeyPrefix(sellerId) {
  return ['root', '', 'leads', String(sellerId || '')];
}


/** Post a staff reply. Variables: `{ sellerId, leadId, message }` → 200 refreshed
 * detail. A closed thread 400s ("Reopen the thread to reply"); that propagates to
 * the caller's `onError` untouched. */
function useLeadReplyMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, leadId, message }) => {
      const data = await apiClient.request({
        url: `/leads/${sellerId}/${leadId}/reply/`,
        method: 'POST',
        payload: { message },
      });
      queryClient.invalidateQueries({ queryKey: leadsKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


/** Change thread status. Variables: `{ sellerId, leadId, status }` → 200 refreshed
 * detail. A same-status POST 400s; the caller only offers valid transitions. */
function useLeadStatusMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, leadId, status }) => {
      const data = await apiClient.request({
        url: `/leads/${sellerId}/${leadId}/status/`,
        method: 'POST',
        payload: { status },
      });
      queryClient.invalidateQueries({ queryKey: leadsKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


/** Convert the lead's buyer into a customer. Variables: `{ sellerId, leadId,
 * body }` where `body` is `{}` | `{ force: true }` | `{ customer: <uuid> }` → 201
 * `{ customer, lead }`. A 409 (`{ duplicates }`) and an already-converted 400 are
 * NOT swallowed — they propagate to the caller for the warn-and-choose flow. */
function useLeadConvertMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, leadId, body }) => {
      const data = await apiClient.request({
        url: `/leads/${sellerId}/${leadId}/convert-customer/`,
        method: 'POST',
        payload: body || {},
      });
      queryClient.invalidateQueries({ queryKey: leadsKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


export {
  leadsKeyPrefix,
  useLeadConvertMutation,
  useLeadReplyMutation,
  useLeadStatusMutation,
};
